import { ConfigError, formatIssues } from './errors'
import { RIDE_IDS, ridesFileSchema } from './rides.schema'
import type { RideId, RidesFile } from './rides.schema'
import { msToTicks, perSecToPerTick } from './ticks'

/** One ride after unit conversion. Fields an ability does not use are 0 / false. */
export interface RideDef {
  readonly id: RideId
  readonly ability: RidesFile['rides'][RideId]['ability']
  readonly speedPct: number
  /** dash: slow strips and mud ignored. */
  readonly ignoresSlow: boolean
  /** shockwave: jump launch percent of normal; radius; stun. */
  readonly jumpVelocityPct: number
  readonly shockRadiusPx: number
  readonly shockStunTicks: number
  /** bulldoze / shell: bump stun. */
  readonly bumpStunTicks: number
  /** glide: fall cap percent of max fall; lift while JUMP is held. */
  readonly fallSpeedPct: number
  readonly glideLiftPxPerTick: number
  /** burrow: how long one burrow lasts. */
  readonly burrowTicks: number
  /** shell: projectiles aimed at the rider break on it. */
  readonly blocksProjectiles: boolean
}

/** Validated `rides.json` (PHASE3 T3.10, D-050/D-056), durations in ticks. */
export interface RideBalance {
  readonly chargeShards: number
  readonly rideDurationTicks: number
  readonly summonTicks: number
  readonly dismountInvulnTicks: number
  readonly maxSpeedPct: number
  readonly shockTierPx: number
  readonly byId: { readonly [K in RideId]: RideDef }
}

function toDef(id: RideId, r: RidesFile['rides'][RideId]): RideDef {
  return Object.freeze({
    id,
    ability: r.ability,
    speedPct: r.speedPct,
    ignoresSlow: r.ability === 'dash' && r.ignoresSlow,
    jumpVelocityPct: r.ability === 'shockwave' ? r.jumpVelocityPct : 100,
    shockRadiusPx: r.ability === 'shockwave' ? r.shockRadiusPx : 0,
    shockStunTicks: r.ability === 'shockwave' ? msToTicks(r.shockStunMs) : 0,
    bumpStunTicks: r.ability === 'bulldoze' || r.ability === 'shell' ? msToTicks(r.bumpStunMs) : 0,
    fallSpeedPct: r.ability === 'glide' ? r.fallSpeedPct : 100,
    glideLiftPxPerTick: r.ability === 'glide' ? perSecToPerTick(r.glideLiftPxPerSec) : 0,
    burrowTicks: r.ability === 'burrow' ? msToTicks(r.burrowMs) : 0,
    blocksProjectiles: r.ability === 'shell' && r.blocksProjectiles,
  })
}

export function loadRides(raw: unknown): RideBalance {
  const parsed = ridesFileSchema.safeParse(raw)
  if (!parsed.success) {
    throw new ConfigError(`Invalid config\n${formatIssues('rides', parsed.error)}`)
  }
  const f = parsed.data
  const byId = Object.fromEntries(RIDE_IDS.map((id) => [id, toDef(id, f.rides[id])])) as {
    [K in RideId]: RideDef
  }
  return Object.freeze({
    chargeShards: f.chargeShards,
    rideDurationTicks: msToTicks(f.rideDurationMs),
    summonTicks: msToTicks(f.summonMs),
    dismountInvulnTicks: msToTicks(f.dismountInvulnMs),
    maxSpeedPct: f.maxSpeedPct,
    shockTierPx: f.shockTierPx,
    byId: Object.freeze(byId),
  })
}
